/*
 * Copyright (c) 2024 Helvia Technologies PC <contact@helvia.io>
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { INTEGRATION_GRANT_TYPES } from '../../../constants/main';

import { Col, Form, Input, Row, Select } from 'antd';
import OpenIdConfigurationFormItem from '../../../components/UI/OpenIdConfigurationFormItem';
import { INTEGRATION_TYPE } from '../../../constants/integrations';

const MS_DYNAMICS_KEYS = [INTEGRATION_TYPE.MICROSOFT_DYNAMICS_CRM, INTEGRATION_TYPE.MICROSOFT_DYNAMICS_KB];

export const useIntegrationFormConfiguration = (integration, formSubmissionErrors, integrationGrantType) => {
  const { t } = useTranslation();

  const initialValues = useMemo(() => {
    let initialValues = {};

    if (integration) {
      initialValues = {
        enabled: true,
        ...integration
      };
      if (MS_DYNAMICS_KEYS.includes(integration.type)) {
        if (integration.updatedAt && integration.version) {
          initialValues.versionMajor = integration.version.split('.')[0];
          initialValues.versionMinor = integration.version.split('.')[1];
        } else {
          initialValues.versionMajor = 'v9';
        }
      }
      if(integration.type === INTEGRATION_TYPE.AZURE_AI_LLM) {
        initialValues.apiVersion = '2023-05-15';
      }
    }

    return initialValues;
  }, [integration]);

  const description = useMemo(() => {
    if (MS_DYNAMICS_KEYS.includes(integration?.type)) {
      return t('Configure to Microsoft Dynamics 365');
    } else if (integration?.type === INTEGRATION_TYPE.OIDC_AUTHENTICATION) {
      return t('Configure to OpenID');
    } else if (integration?.type === INTEGRATION_TYPE.ZENDESK_TICKETING) {
      return t('Configure to Zendesk account');
    }
    return '';
  }, [integration, t]);

  const settings = useMemo(() => {
    let _settings = [];
    if (MS_DYNAMICS_KEYS.includes(integration?.type)) {
      _settings = [
        {
          name: 'baseUrl',
          title: t('Base URL')
        },
        {
          name: 'clientId',
          title: t('Client ID')
        },
        {
          name: 'clientSecret',
          title: t('Client Secret'),
          placeholder: t('Client Secret will not be visible while editing'),
          password: true
        },
        {
          name: 'azureTenantId',
          title: t('Azure Tenant ID')
        },
        {
          name: 'scope',
          title: t('Scope')
        },
        integration.type === INTEGRATION_TYPE.MICROSOFT_DYNAMICS_CRM && {
          name: 'contactLinkTemplate',
          title: t('CRM Contact Page URL')
        },
        {
          title: t('Version'),
          rules: [
            {
              required: true
            }
          ],
          component: (
            <Row gutter={8} align="top" wrap={false}>
              <Col flex="0 0 100px">
                <Form.Item name="versionMajor">
                  <Select
                    size="large"
                    className="fs-base"
                    options={[
                      {
                        label: 'v9',
                        value: 'v9'
                      },
                      {
                        label: 'v8',
                        value: 'v8'
                      }
                    ]}
                    getPopupContainer={triggerNode => triggerNode.parentElement} // Fix drop-menu position issues by rendering it in the parent element
                  />
                </Form.Item>
              </Col>
              <Col flex="auto">
                <Form.Item
                  name="versionMinor"
                  validateTrigger={['onChange', 'onBlur']}
                  rules={[
                    {
                      required: true,
                      type: 'string',
                      whitespace: true,
                      message: t('Please enter a minor version')
                    }
                  ]}>
                  <Input className="w-100 py-2" size="large" placeholder={t('Minor version')} />
                </Form.Item>
              </Col>
            </Row>
          )
        }
      ];
    } else if (integration?.type === INTEGRATION_TYPE.OIDC_AUTHENTICATION) {
      _settings = [
        {
          name: 'configurationUrl',
          title: t('OpenID Configuration URL'),
          component: <OpenIdConfigurationFormItem formSubmissionErrors={formSubmissionErrors} />,
          rules: [{
            required: true
          }]
        },

        {
          name: 'grantType',
          title: t('Grand Type'),
          select: [
            {
              value: INTEGRATION_GRANT_TYPES.AUTHORIZATION_CODE,
              label: t('Authorization Code')
            },
            {
              value: INTEGRATION_GRANT_TYPES.AUTHORIZATION_CODE_PKCE,
              label: t('Authorization Code (with PKCE)')
            }
          ],
          rules: [
            {
              required: true,
              message: t('Please enter a grant type')
            }
          ]
        },
        {
          name: 'clientId',
          title: t('Client ID'),
          rules: [
            {
              required: true,
              message: t('Please enter a client ID')
            }
          ]
        },
        {
          name: 'clientSecret',
          title: t('Client Secret'),
          placeholder: t('Client Secret will not be visible while editing'),
          password: true
        },
        integrationGrantType === INTEGRATION_GRANT_TYPES.AUTHORIZATION_CODE_PKCE && {
          name: 'challengeMethod',
          title: t('Challenge Method'),
          select: [
            {
              value: 'SHA_256',
              label: 'SHA-256'
            },
            {
              value: 'PLAIN',
              label: 'Plain'
            }
          ],
          rules: [
            {
              required: true,
              message: t('Please enter a challenge method')
            }
          ]
        },
        {
          name: 'additionalScopes',
          title: t('Additional Scopes'),
          extra: t('Space separated list of additional scopes')
        }
      ];
    } else if (integration?.type === INTEGRATION_TYPE.ZENDESK_TICKETING) {
      _settings = [
        {
          name: 'baseUrl',
          title: t('Base URL'),
          rules: [
            {
              required: true,
              message: t('Please enter a url')
            }
          ]
        },
        {
          name: 'userName',
          title: t('Username'),
          rules: [
            {
              required: true,
              message: t('Please enter a username')
            }
          ]
        },
        {
          name: 'token',
          title: t('Token'),
          placeholder: t('Client token will not be visible while editing'),
          password: true,
          rules: [
            {
              required: true,
              message: t('Please enter a token')
            }
          ]
        }
      ];
    } else if (integration?.type === INTEGRATION_TYPE.GENESYS_LIVECHAT) {
      _settings = [
        {
          name: 'clientId',
          title: t('Genesys OAuth app client ID'),
          rules: [
            {
              required: true,
              message: t('Please enter the OAuth app client ID')
            }
          ]
        },
        {
          name: 'clientSecret',
          title: t('Genesys OAuth app client secret'),
          password: true,
          rules: [
            {
              required: true,
              message: t('Please enter the OAuth app client secret')
            }
          ]
        },
        {
          name: 'region',
          title: t('Genesys organization region. Domain only.'),
          placeholder: t('e.g. mypurecloud.ie'),
          rules: [
            {
              required: true,
              message: t('Please enter the Genesys organization region')
            }
          ]
        },
        {
          name: 'openIntegrationId',
          title: t('Genesys Open Messaging integration ID'),
          placeholder: t('Sunshine Secret will not be visible while editing'),
          rules: [
            {
              required: true,
              message: t('Please enter the Sunshine Secret')
            }
          ]
        },
        {
          title: t('Queue IDs'),
          rules: [
            {
              required: false
            }
          ],
          component: (
            <Form.Item name="queueIds">
              <Select
                mode="tags"
                size="large"
                tokenSeparators={[',']}
                placeholder={t('Genesys Queue IDs that are going to be subscribed.')}
                options={
                  integration?.queueIds?.length > 0 ? integration?.queueIds.map((tag)=> {
                    return {
                      value: tag,
                      label: tag
                    };
                  }) : null
                }
                getPopupContainer={triggerNode => triggerNode.parentElement} // Fix drop-menu position issues by rendering it in the parent element
              />
            </Form.Item>
          )
        },
        {
          name: 'webhookKey',
          title: t('Webhook Key'),
          placeholder: t('Please enter the Webhook Key'),
          tooltip: t('Go to Apps & Integrations -> Conversation Integrations and view details to show the Shared secret'),
          rules: [
            {
              required: true,
              message: t('Please enter the Webhook Key')
            }
          ]
        }
      ];
    } else if (integration?.type === INTEGRATION_TYPE.ZENDESK_LIVECHAT) {
      _settings = [
        {
          name: 'baseUrl',
          title: t('Zendesk Base URL'),
          rules: [
            {
              required: true,
              message: t('Please enter the Zendesk Base url (without /sc)')
            }
          ]
        },
        {
          name: 'appId',
          title: t('Sunshine App ID'),
          rules: [
            {
              required: true,
              message: t('Please enter the Sunshine App ID')
            }
          ]
        },
        {
          name: 'keyId',
          title: t('Sunshine Conversations Key ID'),
          rules: [
            {
              required: true,
              message: t('Please enter the Sunshine Conversations Key ID')
            }
          ]
        },
        {
          name: 'secret',
          title: t('Sunshine Secret'),
          placeholder: t('Sunshine Secret will not be visible while editing'),
          password: true,
          rules: [
            {
              required: true,
              message: t('Please enter the Sunshine Secret')
            }
          ]
        },
        {
          name: 'webhookKey',
          title: t('Webhook Key'),
          placeholder: t('Please enter the Webhook Key'),
          tooltip: t('Go to Apps & Integrations -> Conversation Integrations and view details to show the Shared secret'),
          rules: [
            {
              required: true,
              message: t('Please enter the Webhook Key')
            }
          ]
        }
      ];
    } else if (integration?.type === INTEGRATION_TYPE.CISCO_LIVECHAT) {
      _settings = [
        {
          name: 'clientId',
          title: t('Client ID'),
          rules: [
            {
              required: true,
              message: t('Please enter a client ID')
            }
          ]
        },
        {
          name: 'clientSecret',
          title: t('Client Secret'),
          placeholder: t('Client Secret will not be visible while editing'),
          password: true,
          rules: [
            {
              required: true,
              message: t('Please enter a client secret')
            }
          ]
        },
        {
          name: 'ccxQueueTag',
          title: t('CCX Queue Tag'),
          rules: [
            {
              required: true,
              message: t('Please enter a CCX queue tag')
            }
          ]
        },
        {
          name: 'chatId',
          title: t('Chat ID'),
          number: true,
          rules: [
            {
              required: true,
              message: t('Please enter a chat ID')
            }
          ]
        },
        {
          name: 'endpoint',
          title: t('Endpoint'),
          rules: [
            {
              required: true,
              message: t('Please enter an endpoint')
            }
          ]
        }
      ];
    } else if (integration?.type === INTEGRATION_TYPE.OPEN_AI_LLM) {
      _settings = [
        {
          name: 'apiKey',
          title: t('API Key'),
          rules: [
            {
              required: true,
              message: t('Please enter the API Key')
            }
          ]
        },
        {
          name: 'project',
          title: t('Project')
        },
        {
          name: 'openAIOrganization',
          title: t('Organization')
        },
        {
          name: 'customUrl',
          title: t('Custom Base URL'),
          rules: [
            {
              type: 'url',
              message: `${t('This field must be a valid url')}.`
            }]
        }
      ];
    } else if (integration?.type === INTEGRATION_TYPE.AZURE_AI_LLM) {
      _settings = [
        {
          name: 'endpoint',
          title: t('Endpoint'),
          rules: [
            {
              required: true,
              message: t('Please enter an endpoint')
            }
          ]
        },
        {
          name: 'apiKey',
          title: t('API Key'),
          password: true,
          rules: [
            {
              required: true,
              message: t('Please enter the API Key')
            }
          ]
        }
      ];
    } else if (integration?.type === INTEGRATION_TYPE.GEMINI_AI_LLM) {
      _settings = [
        {
          name: 'apiKey',
          title: t('API Key'),
          password: true,
          rules: [
            {
              required: true,
              message: t('Please enter the API Key')
            }
          ]
        }
      ];
    }

    return _settings.filter(Boolean);
  }, [integration, integrationGrantType, t, formSubmissionErrors]);

  return { description, settings, initialValues };
};
